import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, endOfMonth, format, startOfDay, startOfMonth } from 'date-fns';
import es from 'date-fns/locale/es';
import { CheckCircle, Filter, Play, XCircle } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Badge, Button, Card, CardContent, DateRangePicker } from '../ui';
import { usePermissions } from '../../hooks/usePermissions';
import { closeSessionsNow, getAllGroups, getAsistenciasBySesion, getAsistenciasCountsBySesiones, getCurrentStaff, getSeriesEntrenamiento, getSesionesByGrupo, getStaffGroups, updateSesion } from '../../lib/supabase';
import { cn } from '../../lib/utils';
import { useModal } from '../../hooks/useModal';
import { formatLocalDate } from '../../lib/dateUtils';
import { useNavigate } from 'react-router-dom';

const STATUS_COLOR = {
  none: 'bg-transparent border border-dashed border-muted-foreground/40',
  en_curso: 'bg-amber-400 border-amber-500',
  terminado: 'bg-slate-900 border-slate-900'
};

const TrainingAgendaMonthly = () => {
  const navigate = useNavigate();
  const { isAdmin, isHeadCoach } = usePermissions();
  const { showConfirm, showError, showSuccess } = useModal();

  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [series, setSeries] = useState([]);
  const [selectedSeriesIds, setSelectedSeriesIds] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [attendanceCounts, setAttendanceCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [closingSessions, setClosingSessions] = useState(false);
  const [previewSession, setPreviewSession] = useState(null);
  const [previewSerie, setPreviewSerie] = useState(null);
  const [previewStats, setPreviewStats] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [finishingPreview, setFinishingPreview] = useState(false);
  const [cellSize, setCellSize] = useState(32);

  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return {
      startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(now), 'yyyy-MM-dd')
    };
  });

  const parseYmdToLocal = useCallback((value) => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 12, 0, 0);
  }, []);

  const rangeStart = useMemo(() => {
    const parsed = parseYmdToLocal(dateRange.startDate);
    return parsed ? startOfDay(parsed) : startOfMonth(new Date());
  }, [dateRange.startDate, parseYmdToLocal]);
  const rangeEnd = useMemo(() => {
    const parsed = parseYmdToLocal(dateRange.endDate);
    return parsed ? startOfDay(parsed) : endOfMonth(new Date());
  }, [dateRange.endDate, parseYmdToLocal]);

  const daysInRange = useMemo(() => {
    const days = [];
    let current = rangeStart;
    while (current <= rangeEnd) {
      days.push(current);
      current = addDays(current, 1);
    }
    return days;
  }, [rangeStart, rangeEnd]);

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      let data = [];
      if (isAdmin || isHeadCoach) {
        const { data: allGroups, error } = await getAllGroups();
        if (error) throw error;
        data = allGroups || [];
      } else {
        const { data: staffData } = await getCurrentStaff();
        const staffId = staffData?.id;
        if (!staffId) {
          setGroups([]);
          setSelectedGroupIds([]);
          return;
        }
        const { data: staffGroups, error } = await getStaffGroups(staffId);
        if (error) throw error;
        data = (staffGroups || []).map((item) => item.grupos).filter(Boolean);
      }

      setGroups(data);
      setSelectedGroupIds(data.map((group) => group.id));
    } catch (error) {
      console.error('Error cargando grupos de entrenamiento:', error);
      setGroups([]);
      setSelectedGroupIds([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isHeadCoach]);

  const loadSeries = useCallback(async () => {
    try {
      const { data, error } = await getSeriesEntrenamiento(null, true);
      if (error) throw error;
      const filtered = (data || []).filter((serie) => {
        if (!serie.grupo_id) return false;
        if (!selectedGroupIds.length) return false;
        return selectedGroupIds.includes(serie.grupo_id);
      });
      setSeries(filtered);
      setSelectedSeriesIds(filtered.map((serie) => serie.id));
    } catch (error) {
      console.error('Error cargando series de entrenamiento:', error);
      setSeries([]);
      setSelectedSeriesIds([]);
    }
  }, [selectedGroupIds]);

  const loadSessions = useCallback(async () => {
    if (!selectedGroupIds.length) {
      setSessions([]);
      setAttendanceCounts({});
      return;
    }
    setLoadingSessions(true);
    try {
      const startDate = format(rangeStart, 'yyyy-MM-dd');
      const endDate = format(rangeEnd, 'yyyy-MM-dd');
      const results = await Promise.all(
        selectedGroupIds.map((groupId) =>
          getSesionesByGrupo({
            groupId,
            startDate,
            endDate,
            tipo: 'entrenamiento'
          })
        )
      );
      const merged = results.flatMap((result) => result.data || []);
      setSessions(merged);
      const sessionIds = merged.map((session) => session?.id).filter(Boolean);
      const { data: counts } = await getAsistenciasCountsBySesiones(sessionIds);
      setAttendanceCounts(counts || {});
    } catch (error) {
      console.error('Error cargando sesiones:', error);
      setSessions([]);
      setAttendanceCounts({});
    } finally {
      setLoadingSessions(false);
    }
  }, [selectedGroupIds, rangeStart, rangeEnd]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    const updateCellSize = () => {
      if (typeof window === 'undefined') return;
      const width = window.innerWidth;
      if (width < 640) {
        setCellSize(26);
      } else if (width < 1024) {
        setCellSize(28);
      } else {
        setCellSize(32);
      }
    };
    updateCellSize();
    window.addEventListener('resize', updateCellSize);
    return () => window.removeEventListener('resize', updateCellSize);
  }, []);

  useEffect(() => {
    // loadSeries y loadSessions limpian solos cuando no hay grupos seleccionados;
    // sin early-return aqui, si no las sesiones/series viejas quedan en pantalla.
    loadSeries();
    loadSessions();
  }, [selectedGroupIds, loadSeries, loadSessions]);

  const filteredSeries = useMemo(() => {
    if (!selectedSeriesIds.length) return [];
    return series.filter((serie) => selectedSeriesIds.includes(serie.id));
  }, [series, selectedSeriesIds]);

  const sessionsBySerieDate = useMemo(() => {
    const map = new Map();
    (sessions || []).forEach((session) => {
      const serieId = session.serie_entrenamiento_id;
      const fecha = session.fecha;
      if (!serieId || !fecha) return;
      const key = `${serieId}__${fecha}`;
      const current = map.get(key);
      if (!current) {
        map.set(key, session);
        return;
      }
      const currentCount = current?.id ? attendanceCounts[current.id] || 0 : 0;
      const nextCount = session?.id ? attendanceCounts[session.id] || 0 : 0;
      if (nextCount > currentCount) {
        map.set(key, session);
      }
    });
    return map;
  }, [sessions, attendanceCounts]);

  const toggleGroup = (groupId) => {
    const exists = selectedGroupIds.includes(groupId);
    const next = exists
      ? selectedGroupIds.filter((id) => id !== groupId)
      : [...selectedGroupIds, groupId];
    setSelectedGroupIds(next);
  };

  const toggleSerie = (serieId) => {
    const exists = selectedSeriesIds.includes(serieId);
    const next = exists
      ? selectedSeriesIds.filter((id) => id !== serieId)
      : [...selectedSeriesIds, serieId];
    setSelectedSeriesIds(next);
  };

  const legend = [
    { id: 'none', label: 'Sin asistencia', className: STATUS_COLOR.none },
    { id: 'en_curso', label: 'En curso', className: STATUS_COLOR.en_curso },
    { id: 'terminado', label: 'Terminado', className: STATUS_COLOR.terminado }
  ];

  const handleCloseSessions = async () => {
    const confirmed = await showConfirm(
      'Terminar entrenamientos',
      'Se terminarán todas las sesiones en curso hasta el día de hoy. ¿Deseas continuar?'
    );
    if (!confirmed) return;
    setClosingSessions(true);
    const { data, error } = await closeSessionsNow();
    setClosingSessions(false);
    if (error) {
      showError('Error', error.message || 'No pudimos cerrar las sesiones.');
      return;
    }
    showSuccess(
      'Sesiones cerradas',
      `Entrenamientos: ${data?.cerradas_entrenamientos ?? 0} · Juegos: ${data?.cerradas_juegos ?? 0}`
    );
  };

  const handleOpenSession = (session, serie) => {
    if (!session || !serie?.grupo_id) return;
    setPreviewSession(session);
    setPreviewSerie(serie);
  };

  const handleGoToTraining = () => {
    if (!previewSession || !previewSerie?.grupo_id) return;
    navigate(`/attendance/${previewSerie.grupo_id}`, {
      state: {
        group: previewSerie.grupos || null,
        serieId: previewSerie.id,
        mode: 'entrenamiento',
        fecha: previewSession.fecha
      }
    });
  };

  const handleClosePreview = () => {
    setPreviewSession(null);
    setPreviewSerie(null);
    setPreviewStats(null);
  };

  const handleFinishPreview = async () => {
    if (!previewSession?.id || finishingPreview) return;
    const confirmed = await showConfirm(
      'Terminar entrenamiento',
      'Se marcará esta sesión como terminada. ¿Deseas continuar?'
    );
    if (!confirmed) return;
    setFinishingPreview(true);
    try {
      const { data, error } = await updateSesion(previewSession.id, { estatus: 'terminado' });
      if (error) throw error;
      setPreviewSession((prev) => (prev ? { ...prev, estatus: data?.estatus || 'terminado' } : prev));
      setSessions((prev) =>
        prev.map((session) =>
          session.id === previewSession.id
            ? { ...session, estatus: data?.estatus || 'terminado' }
            : session
        )
      );
      showSuccess('Sesión terminada', 'La sesión se marcó como terminada.');
    } catch (error) {
      console.error('Error terminando sesión:', error);
      showError('Error', 'No se pudo terminar la sesión.');
    } finally {
      setFinishingPreview(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    const loadPreviewStats = async () => {
      if (!previewSession?.id) {
        if (isActive) setPreviewStats(null);
        return;
      }
      setPreviewLoading(true);
      try {
        const records = await getAsistenciasBySesion(previewSession.id);
        if (!isActive) return;
        const summary = {
          presente: 0,
          retardo: 0,
          ausente: 0
        };
        (records || []).forEach((record) => {
          const key = (record.presente || '').toLowerCase();
          if (summary[key] !== undefined) summary[key] += 1;
        });
        const total = summary.presente + summary.retardo + summary.ausente;
        setPreviewStats({ ...summary, total });
      } catch (error) {
        if (isActive) setPreviewStats(null);
      } finally {
        if (isActive) setPreviewLoading(false);
      }
    };

    loadPreviewStats();

    return () => {
      isActive = false;
    };
  }, [previewSession]);

  return (
    <FeaturedLayout
      title="Agenda de entrenamientos"
      subtitle="Resumen mensual de asistencia por horario de entrenamiento."
    >
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            size="sm"
            className="flex items-center gap-2"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <Filter className="h-4 w-4" />
            Filtros
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={handleCloseSessions}
              disabled={closingSessions}
            >
              Terminar entrenamientos
            </Button>
          )}
          <Badge variant="secondary">
            {format(rangeStart, 'dd MMM', { locale: es })} - {format(rangeEnd, 'dd MMM yyyy', { locale: es })}
          </Badge>
        </div>

        {showFilters && (
          <Card className="border shadow-none bg-muted/30 max-w-full overflow-hidden">
            <CardContent className="p-4 space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Rango de fechas</p>
                <DateRangePicker
                  className="w-full max-w-full"
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  onDateRangeChange={(startDate, endDate) => {
                    if (startDate || endDate) {
                      setDateRange({
                        startDate: startDate || dateRange.startDate,
                        endDate: endDate || dateRange.endDate
                      });
                      return;
                    }
                    const now = new Date();
                    setDateRange({
                      startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
                      endDate: format(endOfMonth(now), 'yyyy-MM-dd')
                    });
                  }}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Grupos</p>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-[11px] text-muted-foreground hover:text-foreground"
                      onClick={() => setSelectedGroupIds([])}
                    >
                      Limpiar
                    </Button>
                    <span className="text-muted-foreground/40">|</span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-[11px]"
                      onClick={() => setSelectedGroupIds(groups.map((group) => group.id))}
                    >
                      Todos
                    </Button>
                  </div>
                </div>
                {groups.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No tienes grupos asignados.</p>
                ) : (
                  <div className="flex flex-wrap gap-2 max-w-full overflow-x-auto pb-1">
                    {groups.map((group) => {
                      const active = selectedGroupIds.includes(group.id);
                      return (
                        <Button
                          key={group.id}
                          type="button"
                          size="sm"
                          variant={active ? 'default' : 'outline'}
                          className="h-8 rounded-full px-3 text-xs font-normal"
                          onClick={() => toggleGroup(group.id)}
                        >
                          {group.nombre_del_grupo}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Horarios activos</p>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-[11px] text-muted-foreground hover:text-foreground"
                      onClick={() => setSelectedSeriesIds([])}
                    >
                      Limpiar
                    </Button>
                    <span className="text-muted-foreground/40">|</span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-[11px]"
                      onClick={() => setSelectedSeriesIds(series.map((serie) => serie.id))}
                    >
                      Todas
                    </Button>
                  </div>
                </div>
                {series.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No hay horarios activos para los grupos seleccionados.</p>
                ) : (
                  <div className="flex flex-wrap gap-2 max-w-full overflow-x-auto pb-1">
                    {series.map((serie) => {
                      const active = selectedSeriesIds.includes(serie.id);
                      return (
                        <Button
                          key={serie.id}
                          type="button"
                          size="sm"
                          variant={active ? 'secondary' : 'outline'}
                          className="h-8 rounded-full px-3 text-xs font-normal"
                          onClick={() => toggleSerie(serie.id)}
                        >
                          {serie.nombre}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          {legend.map((item) => (
            <div key={item.id} className="flex items-center gap-2">
              <span className={cn('inline-flex h-3 w-3 rounded-sm', item.className)} />
              {item.label}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground">Cargando grupos...</div>
        ) : loadingSessions ? (
          <div className="text-sm text-muted-foreground">Cargando sesiones...</div>
        ) : filteredSeries.length === 0 ? (
          <Card className="border shadow-none">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              No hay horarios activos para mostrar con los filtros actuales.
            </CardContent>
          </Card>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="grid grid-cols-[120px_1fr] xs:grid-cols-[160px_1fr] sm:grid-cols-[200px_1fr] lg:grid-cols-[260px_1fr] max-h-[60vh] overflow-y-auto">
              <div className="border-r bg-card">
                <div className="sticky top-0 z-10 border-b bg-card px-3 py-2 text-xs font-semibold text-muted-foreground h-9 flex items-center">
                  Horario
                </div>
                {filteredSeries.map((serie) => {
                  const groupName = serie?.grupos?.nombre_del_grupo || 'Grupo sin nombre';
                  return (
                    <div key={serie.id} className="border-b px-3 py-2 h-11 flex flex-col justify-center">
                      <p className="text-sm font-semibold truncate">{groupName}</p>
                      <p className="text-xs text-muted-foreground truncate">{serie.nombre}</p>
                    </div>
                  );
                })}
              </div>

              <div className="overflow-x-auto">
                <div style={{ minWidth: `${daysInRange.length * cellSize}px` }}>
                  <div
                    className="grid border-b bg-card sticky top-0 z-10 h-9"
                    style={{ gridTemplateColumns: `repeat(${daysInRange.length}, ${cellSize}px)` }}
                  >
                    {daysInRange.map((day) => (
                      <div
                        key={day.toISOString()}
                        className="border-r text-center text-[11px] font-semibold text-muted-foreground flex items-center justify-center"
                      >
                        {format(day, 'd', { locale: es })}
                      </div>
                    ))}
                  </div>

                  {filteredSeries.map((serie) => (
                    <div
                      key={serie.id}
                      className="grid border-b h-11"
                      style={{ gridTemplateColumns: `repeat(${daysInRange.length}, ${cellSize}px)` }}
                    >
                      {daysInRange.map((day) => {
                        const dateKey = format(day, 'yyyy-MM-dd');
                        const session = sessionsBySerieDate.get(`${serie.id}__${dateKey}`);
                        const attendanceCount = session?.id ? attendanceCounts[session.id] || 0 : 0;
                        const shouldShow = session && attendanceCount > 0;
                        const status = shouldShow
                          ? (session.estatus || '').toLowerCase() === 'terminado'
                            ? 'terminado'
                            : 'en_curso'
                          : 'none';
                        const label = shouldShow ? `Estatus: ${session.estatus || 'en_curso'}` : 'Sin asistencia';
                        return (
                          <div
                            key={`${serie.id}-${dateKey}`}
                            className="border-r flex items-center justify-center"
                          >
                            {/* boton nativo a proposito: 12-14px, Button tiene tamano minimo */}
                            <button
                              type="button"
                              onClick={() => {
                                if (shouldShow) handleOpenSession(session, serie);
                              }}
                              className={cn(
                                'rounded-full border',
                                STATUS_COLOR[status],
                                shouldShow ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'
                              )}
                              style={{ width: cellSize <= 26 ? 12 : 14, height: cellSize <= 26 ? 12 : 14 }}
                              title={label}
                              aria-label={label}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {previewSession && previewSerie && (
        <div className="fixed inset-x-3 bottom-3 z-40 w-auto rounded-2xl border bg-card shadow-2xl md:inset-auto md:right-6 md:bottom-6 md:w-full md:max-w-md">
          <div className="flex items-start justify-between border-b px-4 py-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Entrenamiento</p>
              <h3 className="text-lg font-semibold leading-tight">
                {previewSerie?.nombre || 'Horario'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {formatLocalDate(previewSession.fecha, "EEEE d 'de' MMMM yyyy")}
                {previewSession.hora_inicio ? ` · ${previewSession.hora_inicio.slice(0, 5)}` : ''}
                {previewSession.hora_fin ? ` - ${previewSession.hora_fin.slice(0, 5)}` : ''}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClosePreview}>
              <XCircle className="h-5 w-5" />
            </Button>
          </div>
          <div className="space-y-3 px-4 py-3 text-sm">
            <p>
              <span className="font-semibold">Grupo:</span>{' '}
              {previewSerie?.grupos?.nombre_del_grupo || 'Grupo'}
            </p>
            <p>
              <span className="font-semibold">Estado:</span>{' '}
              {(previewSession.estatus || 'programado').replace(/_/g, ' ')}
            </p>
            <div className="space-y-2">
              <p className="font-semibold text-xs uppercase text-muted-foreground">
                Indicadores de asistencia
              </p>
              {previewLoading ? (
                <p className="text-xs text-muted-foreground">Cargando...</p>
              ) : previewStats ? (
                <div className="flex flex-wrap gap-1 text-[10px]">
                  <div className="flex items-center gap-1 rounded-md border bg-emerald-50 px-2 py-1 text-emerald-700">
                    <span className="uppercase text-[10px]">Presentes</span>
                    <span className="font-semibold">{previewStats.presente}</span>
                  </div>
                  <div className="flex items-center gap-1 rounded-md border bg-amber-50 px-2 py-1 text-amber-700">
                    <span className="uppercase text-[10px]">Retardos</span>
                    <span className="font-semibold">{previewStats.retardo}</span>
                  </div>
                  <div className="flex items-center gap-1 rounded-md border bg-rose-50 px-2 py-1 text-rose-700">
                    <span className="uppercase text-[10px]">Ausentes</span>
                    <span className="font-semibold">{previewStats.ausente}</span>
                  </div>
                  <div className="flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-1 text-foreground">
                    <span className="uppercase text-[10px]">Registros</span>
                    <span className="font-semibold">{previewStats.total}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Sin registros aún.</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t px-4 py-3">
            {previewSession?.estatus !== 'terminado' && (
              <Button
                variant="outline"
                onClick={handleFinishPreview}
                disabled={finishingPreview}
                className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                Terminar entrenamiento
              </Button>
            )}
            <Button
              onClick={handleGoToTraining}
              title="Ir al entrenamiento"
              className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <Play className="h-4 w-4" />
              <span>Entrenar</span>
            </Button>
          </div>
        </div>
      )}
    </FeaturedLayout>
  );
};

export default TrainingAgendaMonthly;


